import CloseIcon from '@mui/icons-material/Close';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import SaveIcon from '@mui/icons-material/Save';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import RestoreIcon from '@mui/icons-material/Restore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import {
    Alert,
    AppBar,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    IconButton,
    Stack,
    Toolbar,
    Tooltip,
    Typography
} from '@mui/material';
import Editor from '@monaco-editor/react';
import type { editor } from 'monaco-editor';
import React from 'react';

export type TemplateEditorMessage = { type: 'success' | 'error'; text: string } | null;

export type WebGameTemplateHtmlEditorDialogProps = {
    open: boolean;
    onClose: () => void;
    /** Current HTML buffer (controlled). */
    value: string;
    onChange: (next: string) => void;
    gameCode: string;
    templateFileName?: string;
    source?: 'override' | 'classpath';
    updatedAtLabel?: string;
    loading: boolean;
    saving: boolean;
    message: TemplateEditorMessage;
    canReset: boolean;
    onSave: () => void | Promise<void>;
    onReloadFromServer: () => void | Promise<void>;
    onResetToClasspath?: () => void | Promise<void>;
    onReloadPreview?: () => void;
};

export default function WebGameTemplateHtmlEditorDialog(props: WebGameTemplateHtmlEditorDialogProps) {
    const {
        open,
        onClose,
        value,
        onChange,
        gameCode,
        templateFileName,
        source,
        updatedAtLabel,
        loading,
        saving,
        message,
        canReset,
        onSave,
        onReloadFromServer,
        onResetToClasspath,
        onReloadPreview
    } = props;

    const editorRef = React.useRef<editor.IStandaloneCodeEditor | null>(null);
    const [wordWrap, setWordWrap] = React.useState(true);
    const [minimap, setMinimap] = React.useState(true);
    const [theme, setTheme] = React.useState<'vs-dark' | 'light'>('vs-dark');

    const runEditorAction = React.useCallback((id: string) => {
        const ed = editorRef.current;
        if (!ed) return;
        ed.focus();
        ed.getAction(id)?.run();
    }, []);

    const handleFormat = React.useCallback(() => {
        runEditorAction('editor.action.formatDocument');
    }, [runEditorAction]);

    const handleSelectAll = React.useCallback(() => {
        runEditorAction('editor.action.selectAll');
    }, [runEditorAction]);

    React.useEffect(() => {
        if (!open) return;
        window.setTimeout(() => editorRef.current?.layout(), 0);
    }, [open, wordWrap, minimap, theme]);

    return (
        <Dialog fullScreen open={open} onClose={onClose} disableEscapeKeyDown={saving}>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: 'background.default' }}>
                <AppBar position="relative" color="default" elevation={1} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Toolbar variant="dense" sx={{ gap: 1, flexWrap: 'wrap', py: 0.5 }}>
                        <IconButton edge="start" color="inherit" onClick={onClose} aria-label="Close editor" size="small" disabled={saving}>
                            <CloseIcon />
                        </IconButton>
                        <Typography variant="subtitle1" component="div" fontWeight={700} sx={{ flexGrow: 0 }}>
                            Template HTML — {gameCode}
                        </Typography>
                        {templateFileName && (
                            <Chip size="small" label={templateFileName} variant="outlined" sx={{ maxWidth: 280 }} />
                        )}
                        {source && (
                            <Chip
                                size="small"
                                label={source === 'override' ? 'Mongo override' : 'Classpath default'}
                                color={source === 'override' ? 'warning' : 'default'}
                            />
                        )}
                        {updatedAtLabel && (
                            <Typography variant="caption" color="text.secondary">
                                {updatedAtLabel}
                            </Typography>
                        )}
                        <Box sx={{ flex: 1 }} />
                        <Tooltip title="Reload from games server">
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={loading ? <CircularProgress size={14} /> : <RestartAltIcon />}
                                onClick={() => void onReloadFromServer()}
                                disabled={loading || saving}
                            >
                                Reload
                            </Button>
                        </Tooltip>
                        {onResetToClasspath && (
                            <Tooltip title="Remove Mongo override">
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="warning"
                                    startIcon={<RestoreIcon />}
                                    onClick={() => void onResetToClasspath()}
                                    disabled={!canReset || loading || saving}
                                >
                                    Reset classpath
                                </Button>
                            </Tooltip>
                        )}
                        {onReloadPreview && (
                            <Button size="small" variant="text" startIcon={<VisibilityIcon />} onClick={onReloadPreview}>
                                Refresh iframe
                            </Button>
                        )}
                        <Button
                            size="small"
                            variant="contained"
                            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                            onClick={() => void onSave()}
                            disabled={saving || loading || !value.trim()}
                        >
                            Save
                        </Button>
                    </Toolbar>
                </AppBar>

                {message && (
                    <Box sx={{ px: 2, pt: 1 }}>
                        <Alert severity={message.type}>{message.text}</Alert>
                    </Box>
                )}

                <Stack direction="row" spacing={1} sx={{ px: 2, py: 1, flexWrap: 'wrap', alignItems: 'center', borderBottom: 1, borderColor: 'divider', bgcolor: 'action.hover' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                        Editor (Monaco) · same as VS Code shortcuts where supported
                    </Typography>
                    <Tooltip title="Ctrl+F / ⌘F Find · Ctrl+H Replace · Ctrl+G Go to line">
                        <Chip size="small" label="Find / Replace / Line" variant="outlined" />
                    </Tooltip>
                    <Button size="small" variant="text" startIcon={<SelectAllIcon />} onClick={handleSelectAll}>
                        Select all
                    </Button>
                    <Button size="small" variant="text" startIcon={<FormatAlignLeftIcon />} onClick={handleFormat}>
                        Format
                    </Button>
                    <Button size="small" variant={wordWrap ? 'contained' : 'outlined'} onClick={() => setWordWrap(w => !w)}>
                        Word wrap
                    </Button>
                    <Button size="small" variant={minimap ? 'contained' : 'outlined'} onClick={() => setMinimap(m => !m)}>
                        Minimap
                    </Button>
                    <Tooltip title="Toggle light/dark theme">
                        <IconButton
                            size="small"
                            onClick={() => setTheme(t => (t === 'vs-dark' ? 'light' : 'vs-dark'))}
                            aria-label="Toggle theme"
                        >
                            {theme === 'vs-dark' ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
                        </IconButton>
                    </Tooltip>
                </Stack>

                <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
                    {loading && (
                        <Box
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: 'rgba(0,0,0,0.12)',
                                zIndex: 2
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    )}
                    <Editor
                        height="100%"
                        theme={theme}
                        path={`webgame-template-${gameCode}.html`}
                        defaultLanguage="html"
                        value={value}
                        onChange={v => onChange(v ?? '')}
                        onMount={ed => {
                            editorRef.current = ed;
                            ed.updateOptions({
                                tabSize: 2,
                                insertSpaces: true,
                                scrollBeyondLastLine: false,
                                renderWhitespace: 'selection',
                                bracketPairColorization: { enabled: true },
                                smoothScrolling: true,
                                padding: { top: 12, bottom: 12 }
                            });
                        }}
                        options={{
                            readOnly: loading || saving,
                            minimap: { enabled: minimap },
                            wordWrap: wordWrap ? 'on' : 'off',
                            lineNumbers: 'on',
                            folding: true,
                            glyphMargin: true,
                            automaticLayout: true,
                            fontSize: 13,
                            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                            quickSuggestions: true,
                            suggestOnTriggerCharacters: true,
                            formatOnPaste: false,
                            mouseWheelZoom: true
                        }}
                    />
                </Box>
            </Box>
        </Dialog>
    );
}
